import Calendar from '@components/calendar/Calendar';
import type { AvailabilityResponse } from '@components/calendar/types';
import { useStorefrontEmptyStates } from '@feature-flags/hooks/Storefront/useStorefrontEmptyStates';
import { useUserEngagementInteractivity } from '@feature-flags/hooks/Storefront/useUserEngagementInteractivity';
import { useAppSelector } from '@redux/hooks';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import RfqButton from '@tkmp/rfq/shared/cta_box/RfqButton';
import { handleFieldChange } from '@tkmp/rfq/utils';
import { TextTitle } from '@tkww/orion-web-ds-text';
import { TextBody, TextCaption } from '@tkww/orion-web-ds-text';
import { Bone, Skeleton } from '@xo-union/tk-ui-skeleton';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import React from 'react';
import type { State } from 'types/redux';
import type { NavItemProps } from '../../containers/nav/types';
import { EmptyStateSearchLink } from '../EmptyStateSearchLink/EmptyStateSearchLink';
import { AvailabilityLegend } from './AvailabilityLegend';
import Styles from './AvailabilitySection.scss';
import { availabilityLoading } from './availability-atoms';
import { useTrackAvailabilityCalendar } from './hooks/use-track-availability-calendar';

const DATE_FORMAT = 'yyyy-MM-dd';

export type AvailabilitySectionProps = {
	availabilityData: AvailabilityResponse | null;
};

const ConfettiIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 220 220"
		fill="none"
	>
		<title>Confetti</title>
		<path
			d="M10.7328 52.0535C10.7328 52.0535 54.5049 101.807 0.945312 152.92C0.945312 152.92 77.3426 131.714 84.6833 208.927C84.6833 208.927 122.746 148.57 184.19 194.246C184.19 194.246 145.311 133.617 214.912 108.06C214.912 108.06 145.583 81.6883 174.946 27.8564C174.946 27.8564 110.239 71.085 80.333 0.94043C80.6049 0.94043 80.8768 73.5319 10.7328 52.0535Z"
			fill="#FF44CB"
		/>
	</svg>
);

const AvailabilitySectionEmptyState = () => (
	<>
		<TextTitle
			size="xs"
			className={Styles.sectionTitle}
			data-testid="availability-empty"
		>
			Availability
		</TextTitle>
		<EmptyStateSearchLink
			header="No availability details yet"
			text="Looking for the perfect match?"
			linkText="Continue browsing vendors"
			storefrontSection="Availability"
		/>
	</>
);

const AvailabilitySection = ({
	availabilityData,
	className,
}: AvailabilitySectionProps & NavItemProps) => {
	const formContext = useInlineRfqForm({
		initiator: 'Availability Calendar CTA',
	});

	const isEmptyStates = useStorefrontEmptyStates();
	const isLoading = useAtomValue(availabilityLoading);
	const shouldShowAvailabilityEmptyStates = isEmptyStates && !availabilityData;
	const weddingDate =
		useAppSelector((state: State) => state?.membership?.member?.wedding_date) ||
		null;
	const userEngagementInteractivityAssignmentVariant =
		useUserEngagementInteractivity();
	const trackAvailabilityCalendar = useTrackAvailabilityCalendar();

	const renderRfqButton = () => (
		<RfqButton
			key="availability-rfq-button"
			size="sm"
			color="secondary"
			label="Ask about a specific date"
			handleCTAClick={formContext.openModal}
			styleName={Styles.availabilityRfqButton}
		/>
	);

	const renderRfqBox = () => (
		<div className="availability-rfq-box">
			<div className={Styles.sectionRfq}>
				<div className={Styles.sectionRfqIcon}>
					<ConfettiIcon />
				</div>
				<TextBody size="md" bold className={Styles.sectionRfqTitle}>
					Lock in your date
				</TextBody>
				<TextCaption size={'lg'} className={Styles.sectionRfqCaption}>
					Check availability and secure your spot today.
				</TextCaption>
				{renderRfqButton()}
			</div>
		</div>
	);

	const handleOnDayClick = (day: number | Date) => {
		const dayFormatted = format(day, DATE_FORMAT);
		const weddingDateValue = formContext.values.weddingDate;

		setTimeout(() => {
			handleFieldChange({
				cb: formContext.setFieldValue,
				data: weddingDateValue,
				name: 'weddingDate',
				value: dayFormatted,
			});
		}, 10);

		formContext.openModal();
	};

	if (!isEmptyStates && !availabilityData) {
		return null;
	}

	if (isLoading) {
		return (
			<>
				<TextTitle size="xs" className={Styles.sectionTitle}>
					Availability
				</TextTitle>
				<Skeleton>
					<Bone style={{ height: 318, width: '100%', borderRadius: 8 }} />
				</Skeleton>
			</>
		);
	}

	return shouldShowAvailabilityEmptyStates ? (
		<AvailabilitySectionEmptyState />
	) : (
		<div className={classNames(Styles.availabilitySectionContainer, className)}>
			<RfqModal context={formContext} />
			<TextTitle
				size="xs"
				className={classNames(Styles.sectionTitle, Styles.sectionTitleSmall)}
				data-testid="availability"
			>
				Availability
			</TextTitle>
			<Calendar
				availabilityData={availabilityData}
				onDayClick={(day) => handleOnDayClick(day)}
				weddingDate={weddingDate}
				trackCalendar={trackAvailabilityCalendar}
			/>
			<AvailabilityLegend />
			<TextBody size="md" className={Styles.sectionCaption}>
				{userEngagementInteractivityAssignmentVariant
					? 'Recent changes may not be reflected in this calendar—reach out to this vendor to confirm their availability.'
					: 'Reach out to this vendor to confirm their availability, as recent changes may not be reflected in this calendar.'}
			</TextBody>
			{userEngagementInteractivityAssignmentVariant
				? renderRfqBox()
				: renderRfqButton()}
		</div>
	);
};

export default AvailabilitySection;
